import { sb, SUPABASE_URL, SUPABASE_ANON_KEY } from '../supabase';

export function isManagedRequest(request) {
  // The canonical signal on the requests table is sourcing_mode='managed'
  // (factory quote-requests + wizard managed requests). `type`/`is_managed` are
  // kept for any legacy/normalized shapes that set them.
  return request?.type === 'managed'
    || request?.is_managed === true
    || String(request?.sourcing_mode || '') === 'managed';
}

// One source of truth for a request's user-facing TYPE, used by both the buyer
// dashboard (badge + timeline) and the admin queue (badge + adaptive detail).
//   factory → quote from ONE known factory (admin relays)
//   idea    → idea-to-product (AI tool → sample development)
//   managed → concierge sourcing (admin matches suppliers)
//   direct  → open RFQ (admin reviews, then suppliers bid)
export function requestType(request) {
  const kind = String(request?.request_kind || '');
  if (kind === 'factory') return 'factory';
  if (kind === 'idea_to_product') return 'idea';
  if (kind === 'managed' || isManagedRequest(request)) return 'managed';
  return 'direct';
}

export function buildManagedBriefRow({ requestId, buyerId, brief }) {
  if (!brief) return null;

  return {
    request_id: requestId,
    buyer_id: buyerId,
    ai_status: brief.ai_status || 'ready',
    admin_review_status: 'pending',
    supplier_brief: brief.supplier_brief || brief.cleaned_description || '',
    admin_internal_notes: brief.admin_internal_notes ?? null,
    admin_follow_up_question: brief.admin_follow_up_question ?? null,
    priority: brief.priority || 'normal',
    extracted_specs: brief.extracted_specs || [],
    cleaned_description: brief.cleaned_description || '',
    category: brief.category || 'other',
    ai_confidence: brief.ai_confidence || 'medium',
    ai_output: brief.ai_output || {},
  };
}

// Resolve the supplier-facing brief text in the viewer's language.
// Prefers the trilingual ai_output.supplier_brief_all cache; falls through
// to the singular supplier_brief column when the cache is missing or cleared
// (e.g., after an admin edit invalidates the AI translations).
export function getLocalizedSupplierBrief(brief, lang) {
  if (!brief) return null;
  const byLang = brief.ai_output?.supplier_brief_all;
  if (byLang && (byLang[lang] || byLang.en || byLang.ar || byLang.zh)) {
    return byLang[lang] || byLang.en || byLang.ar || byLang.zh;
  }
  return brief.supplier_brief || null;
}

export function getManagedVerificationLevel(profiles, lang = 'ar') {
  // Determine verification level based on supplier profile
  if (!profiles) return 'basic';

  const status = profiles?.status;
  const rating = profiles?.rating;
  const dealsCompleted = profiles?.deals_completed;

  if (status === 'verified' && rating >= 4.5 && dealsCompleted > 10) {
    return 'premium';
  }
  if (status === 'verified' || status === 'approved') {
    return 'verified';
  }
  return 'basic';
}

export function getManagedMatchGroup(request) {
  // Group managed requests for supplier matching
  if (!request) return 'general';

  const category = request.category;
  const priority = request.managed_priority || 'normal';

  if (priority === 'urgent') return 'urgent';
  if (category === 'electronics') return 'electronics';
  if (category === 'furniture') return 'furniture';
  if (category === 'clothing') return 'clothing';
  if (category === 'building') return 'building';
  if (category === 'food') return 'food';

  return 'general';
}

function pickByLang(record, field, lang) {
  if (!record) return '';
  return record[`${field}_${lang}`] || record[`${field}_en`] || record[`${field}_ar`] || record[field] || '';
}

function buildHeuristicBrief({ request, lang, aiStatus = 'ready' }) {
  const description = request?.description || '';
  const category = request?.category || 'other';

  const extractedSpecs = [];
  if (description.includes('كجم') || description.includes('kg')) extractedSpecs.push({ key: 'weight', value: null, unit: 'kg', confidence: 'low' });
  if (description.includes('لون') || description.includes('color')) extractedSpecs.push({ key: 'color', value: null, unit: null, confidence: 'low' });
  if (description.includes('مقاس') || description.includes('size')) extractedSpecs.push({ key: 'size', value: null, unit: null, confidence: 'low' });
  if (description.includes('مادة') || description.includes('material')) extractedSpecs.push({ key: 'material', value: null, unit: null, confidence: 'low' });

  const priority = request?.response_deadline ? 'urgent' : 'normal';
  const aiConfidence = description.length > 50 ? 'high' : description.length > 20 ? 'medium' : 'low';

  const briefBody = description.trim().substring(0, 200);
  const supplierBriefAr = `طلب ${category} من خلال معبر. ${briefBody}`;
  const supplierBriefEn = `Request for ${category} through Maabar. ${briefBody}`;
  const supplierBriefZh = `通过 Maabar 采购 ${category}。${briefBody}`;
  const supplierBriefByLang = { ar: supplierBriefAr, en: supplierBriefEn, zh: supplierBriefZh };

  return {
    ai_status: aiStatus,
    priority,
    extracted_specs: extractedSpecs,
    cleaned_description: description.trim(),
    category,
    ai_confidence: aiConfidence,
    supplier_brief: supplierBriefByLang[lang] || supplierBriefEn,
    admin_follow_up_question: null,
    admin_internal_notes: null,
    ai_output: {
      generated_at: new Date().toISOString(),
      model: 'heuristic-fallback',
      prompt_version: 'managed_brief.v1',
      supplier_brief_all: supplierBriefByLang,
    },
  };
}

function mergePriority(buyerPriority, aiPriority) {
  if (buyerPriority === 'urgent') return 'urgent';
  return aiPriority === 'urgent' ? 'urgent' : 'normal';
}

export async function generateManagedBriefWithAI({ request, lang = 'ar', requestId = null, buyerId = null }) {
  const payload = {
    language: lang,
    title: pickByLang(request, 'title', lang),
    description: pickByLang(request, 'description', lang) || request?.description || '',
    category: request?.category || 'other',
    quantity: request?.quantity ?? null,
    budget: request?.budget_per_unit ?? null,
    budget_currency: request?.budget_currency || null,
    response_deadline: request?.response_deadline || null,
    // When present, maabar-ai persists the brief + advances the request to
    // admin_review server-side, so completion no longer depends on the trader's
    // browser staying open.
    requestId,
    buyerId,
  };

  try {
    // The trader's own token, not the anon key: maabar-ai persists the brief
    // server-side only for the request's buyer, identified from this JWT
    // (security review H4). Falls back to the anon key, which still returns the
    // brief — the caller's own upsert then persists it.
    let bearer = SUPABASE_ANON_KEY;
    try {
      const { data } = await sb.auth.getSession();
      if (data?.session?.access_token) bearer = data.session.access_token;
    } catch { /* anon fallback */ }
    const response = await fetch(`${SUPABASE_URL}/functions/v1/maabar-ai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify({ task: 'managed_brief', payload }),
    });

    if (!response.ok) {
      throw new Error(`maabar-ai managed_brief failed: ${response.status}`);
    }

    const data = await response.json();
    const result = data?.result;
    if (!result || typeof result !== 'object') {
      throw new Error('maabar-ai managed_brief: empty result');
    }

    const supplierBriefAll = (result.supplier_brief && typeof result.supplier_brief === 'object')
      ? result.supplier_brief
      : null;
    const supplierBriefText = supplierBriefAll
      ? (supplierBriefAll[lang] || supplierBriefAll.en || supplierBriefAll.ar || '')
      : (typeof result.supplier_brief === 'string' ? result.supplier_brief : '');

    const aiPriority = result.priority === 'urgent' ? 'urgent' : 'normal';
    const priority = mergePriority(request?.managed_priority, aiPriority);

    return {
      ai_status: 'ready',
      priority,
      extracted_specs: Array.isArray(result.extracted_specs) ? result.extracted_specs : [],
      cleaned_description: typeof result.cleaned_description === 'string' ? result.cleaned_description : (request?.description || '').trim(),
      category: result.category || request?.category || 'other',
      ai_confidence: ['high', 'medium', 'low'].includes(result.ai_confidence) ? result.ai_confidence : 'medium',
      supplier_brief: supplierBriefText || (request?.description || '').trim(),
      admin_follow_up_question: typeof result.admin_follow_up_question === 'string' ? result.admin_follow_up_question : null,
      admin_internal_notes: typeof result.admin_internal_notes === 'string' ? result.admin_internal_notes : null,
      ai_output: {
        generated_at: new Date().toISOString(),
        model: 'maabar-ai',
        prompt_version: 'managed_brief.v1',
        supplier_brief_all: supplierBriefAll,
      },
    };
  } catch (error) {
    console.error('generateManagedBriefWithAI error:', error);
    return buildHeuristicBrief({ request, lang, aiStatus: 'failed' });
  }
}
